"use client";

import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { EmblaCarouselType } from "embla-carousel";

import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn, formatSectionTitle } from "@/lib/utils";

interface MetaItem {
  id: string;
  meta_image: string;
  meta_alt_image?: string;
  meta_title?: string;
  meta_sub_title?: string;
  meta_priority: number;
  meta_status?: boolean;
}

interface Accreditation1000Data {
  id: number;
  page_id?: number;
  /** MySQL booleans may arrive as 0/1 */
  status?: boolean | number;
  title?: string;
  background_image_desktop?: string;
  background_image_mobile?: string;
  metadata?: MetaItem[];
}

interface Accreditation1000Props {
  /** Flat row from API, or `{ data: row }` from some admin previews */
  data: Accreditation1000Data | { data: Accreditation1000Data };
}

function resolveAccreditationPayload(
  data: Accreditation1000Props["data"]
): Accreditation1000Data | undefined {
  if (!data || typeof data !== "object") return undefined;
  const anyData = data as Record<string, unknown>;
  if ("data" in anyData && anyData.data != null && typeof anyData.data === "object") {
    return anyData.data as Accreditation1000Data;
  }
  return data as Accreditation1000Data;
}

function AccreditationCard({ item }: { item: MetaItem }) {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center bg-transparent text-center">
      <div className="flex flex-col items-center">
        <div className="relative flex h-[90px] w-[240px] max-w-full items-center justify-center py-[10px] min-[768px]:py-0">
          {item.meta_image && (
            <Image
              src={item.meta_image}
              alt={item.meta_alt_image || "Accreditation"}
              width={140}
              height={88}
              className="mx-auto object-contain"
            />
          )}
        </div>
        <div className="my-[16px] h-[2px] w-[204px] max-w-[85%] bg-[#FF9A9A8F]" />
        <div className="mt-4 flex h-auto w-[184px] flex-col items-center gap-2 min-[768px]:mt-0 min-[768px]:h-[120px] min-[768px]:w-[210px]">
          {item.meta_title && (
            <h3 className="text-center text-[24px] font-bold leading-normal text-[#FFFFFF] min-[768px]:text-[28px]">
              {item.meta_title}
            </h3>
          )}
          {item.meta_sub_title && (
            <p
              className="w-[184px] max-w-full text-center text-[18px] font-normal leading-normal text-[#FFFFFF]"
              dangerouslySetInnerHTML={{
                __html: formatSectionTitle(item.meta_sub_title, "font-medium text-[#FFFFFF]"),
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const MD_BREAKPOINT = "(min-width: 768px)";

const Accreditation1000 = ({ data }: Accreditation1000Props) => {
  const section = resolveAccreditationPayload(data);
  const {
    title = "",
    background_image_desktop = "",
    background_image_mobile = "",
    metadata = [],
  } = section || {};

  const elementRef = useRef<HTMLElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMdUp, setIsMdUp] = useState(false);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const sortedMetadata = useMemo(() => {
    if (!metadata?.length) return [];
    return [...metadata]
      .filter((item) => item?.meta_status == true)
      .sort((a, b) => a.meta_priority - b.meta_priority);
  }, [metadata]);

  useLayoutEffect(() => {
    const mq = window.matchMedia(MD_BREAKPOINT);
    setIsMdUp(mq.matches);
    const onChange = () => setIsMdUp(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    // loop must be false so canScrollPrev / canScrollNext reflect real edges for arrow visibility
    loop: false,
    dragFree: false,
    slidesToScroll: 1,
  });

  const updateScrollEdges = useCallback((api: EmblaCarouselType) => {
    setCanScrollPrev(api.canScrollPrev());
    setCanScrollNext(api.canScrollNext());
  }, []);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const syncIndex = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    const syncEdges = () => updateScrollEdges(emblaApi);
    const syncAll = () => {
      syncIndex();
      syncEdges();
    };
    syncAll();
    emblaApi.on("select", syncAll);
    emblaApi.on("reInit", syncAll);
    emblaApi.on("settle", syncEdges);
    emblaApi.on("resize", syncEdges);
    return () => {
      emblaApi.off("select", syncAll);
      emblaApi.off("reInit", syncAll);
      emblaApi.off("settle", syncEdges);
      emblaApi.off("resize", syncEdges);
    };
  }, [emblaApi, updateScrollEdges]);

  useEffect(() => {
    if (!emblaApi) return;
    const handleResize = () => emblaApi.reInit();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [emblaApi]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, sortedMetadata]);

  useEffect(() => {
    if (elementRef.current) {
      FireWidgetViewedEvent(elementRef, "accreditation_section");
    }
  }, []);

  const active = section?.status === true || section?.status === 1;
  if (!active) return null;
  if (!title || sortedMetadata.length === 0) return null;

  const showDots = sortedMetadata.length > 1;
  /** When true, carousel cannot scroll by item count — arrows stay visible but disabled. */
  const isScrollLockedByItemCount = isMdUp
    ? sortedMetadata.length <= 3
    : sortedMetadata.length <= 1;
  const isPrevDisabled = isScrollLockedByItemCount || !canScrollPrev;
  const isNextDisabled = isScrollLockedByItemCount || !canScrollNext;

  return (
    <section
      ref={elementRef}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "relative mx-auto flex w-full items-center justify-center overflow-hidden py-[50px]",
        "bg-cover bg-center bg-no-repeat",
        "[background-image:var(--acc-bg-mobile)] min-[768px]:[background-image:var(--acc-bg-desktop)]"
      )}
      style={
        {
          fontFamily: "Poppins, sans-serif",
          "--acc-bg-mobile": `url("${background_image_mobile || background_image_desktop}")`,
          "--acc-bg-desktop": `url("${background_image_desktop || background_image_mobile}")`,
        } as React.CSSProperties
      }
    >
      <div className="flex w-full flex-col items-center justify-center min-[1024px]:w-screen">
        <div className="relative z-10 mx-auto flex flex-col gap-1.5 min-[768px]:gap-4">
          <h2
            className="text-center text-[28px] font-semibold leading-normal text-[#FFFFFF] min-[768px]:text-[32px]"
            dangerouslySetInnerHTML={{
              __html: formatSectionTitle(title, "font-bold text-[#FFFFFF]"),
            }}
          />
        </div>

        <div className="relative z-10 mt-[42px] w-full max-w-[360px] px-12 pb-[50px] min-[768px]:max-w-[min(100%,960px)] min-[768px]:px-14 min-[1024px]:px-16">
          <button
            type="button"
            aria-label="Previous accreditation"
            aria-disabled={isPrevDisabled}
            disabled={isPrevDisabled}
            onClick={scrollPrev}
            className={cn(
              "absolute left-0 top-[36%] z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#FFFFFF] shadow-md min-[768px]:h-11 min-[768px]:w-11",
              isPrevDisabled && "cursor-not-allowed opacity-40"
            )}
          >
            <ArrowLeft className="h-5 w-5 text-[#E1473F] min-[768px]:h-6 min-[768px]:w-6" strokeWidth={2.5} />
          </button>

          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex touch-pan-y min-[768px]:-ml-6">
              {sortedMetadata.map((item, index) => (
                <div
                  className="min-w-0 shrink-0 grow-0 basis-full px-1 min-[768px]:basis-1/3 min-[768px]:pl-6 min-[768px]:pr-0"
                  key={`${item.id}-${index}`}
                >
                  <AccreditationCard item={item} />
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            aria-label="Next accreditation"
            aria-disabled={isNextDisabled}
            disabled={isNextDisabled}
            onClick={scrollNext}
            className={cn(
              "absolute right-0 top-[36%] z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[#FFFFFF] shadow-md min-[768px]:h-11 min-[768px]:w-11",
              isNextDisabled && "cursor-not-allowed opacity-40"
            )}
          >
            <ArrowRight className="h-5 w-5 text-[#E1473F] min-[768px]:h-6 min-[768px]:w-6" strokeWidth={2.5} />
          </button>

          {/* Mobile only: one dot per accreditation item */}
          {showDots && (
            <div
              className="mt-6 flex justify-center gap-2 min-[768px]:hidden"
              role="tablist"
              aria-label="Accreditation slides"
            >
              {sortedMetadata.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={selectedIndex === i}
                  aria-label={`Go to accreditation ${i + 1}`}
                  onClick={() => emblaApi?.scrollTo(i)}
                  className={cn(
                    "h-2 w-2 rounded-full transition-all duration-300",
                    selectedIndex === i
                      ? "scale-125 bg-[#FFFFFF]"
                      : "bg-[#FFFFFF]/45 hover:bg-[#FFFFFF]/70"
                  )}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Accreditation1000;
